/**
 * Mention Configuration
 *
 * Configuration and factory functions for the Tiptap mention extension.
 * Separated from React components for fast refresh compatibility.
 */

import { ReactRenderer } from '@tiptap/react';
import Mention from '@tiptap/extension-mention';
import tippy from 'tippy.js';
import type { Instance as TippyInstance } from 'tippy.js';
import type { SuggestionProps, SuggestionKeyDownProps } from '@tiptap/suggestion';
import type { Editor } from '@tiptap/core';
import type { User } from '@wmp/shared/types';
import type { MentionListRef } from './MentionList';

// We need to dynamically import MentionList to avoid circular dependencies
// and to keep the React component in a separate file for fast refresh
let MentionListComponent: React.ComponentType<{
  items: User[];
  command: (user: User) => void;
  query: string;
}> | null = null;

// Lazy load the MentionList component
async function getMentionListComponent() {
  if (!MentionListComponent) {
    const module = await import('./MentionList');
    MentionListComponent = module.MentionList;
  }
  return MentionListComponent;
}

// Create a configured mention extension with custom suggestion rendering
export function createMentionExtension(
  fetchUsers: (query: string) => Promise<User[]> | User[]
) {
  return Mention.configure({
    HTMLAttributes: {
      class: 'mention',
    },
    suggestion: {
      char: '@',
      allowSpaces: false,

      items: async ({ query }: { query: string }) => {
        return await fetchUsers(query);
      },

      render: () => {
        let component: ReactRenderer<MentionListRef> | null = null;
        let popup: TippyInstance[] | null = null;

        return {
          onStart: async (props: SuggestionProps<User>) => {
            if (!props.clientRect) {
              return;
            }

            const MentionList = await getMentionListComponent();

            component = new ReactRenderer(MentionList, {
              props: {
                items: props.items,
                command: (user: User) => {
                  props.command({
                    id: user.id,
                    label: user.name,
                  });
                },
                query: props.query,
              },
              editor: props.editor as Editor,
            });

            popup = tippy('body', {
              getReferenceClientRect: props.clientRect as () => DOMRect,
              appendTo: () => document.body,
              content: component.element,
              showOnCreate: true,
              interactive: true,
              trigger: 'manual',
              placement: 'bottom-start',
              theme: 'mention',
              maxWidth: '300px',
            });
          },

          onUpdate: (props: SuggestionProps<User>) => {
            component?.updateProps({
              items: props.items,
              command: (user: User) => {
                props.command({
                  id: user.id,
                  label: user.name,
                });
              },
              query: props.query,
            });

            if (!props.clientRect) {
              return;
            }

            popup?.[0]?.setProps({
              getReferenceClientRect: props.clientRect as () => DOMRect,
            });
          },

          onKeyDown: (props: SuggestionKeyDownProps) => {
            if (props.event.key === 'Escape') {
              popup?.[0]?.hide();
              return true;
            }

            return component?.ref?.onKeyDown(props.event) ?? false;
          },

          onExit: () => {
            popup?.[0]?.destroy();
            component?.destroy();
          },
        };
      },
    },
  });
}

// Default export for simple cases where mentions aren't needed
export const MentionExtension = Mention.configure({
  HTMLAttributes: {
    class: 'mention',
  },
  suggestion: {
    char: '@',
    items: () => [],
  },
});

// Add tippy styles for mention dropdown
const tippyStyles = `
.tippy-box[data-theme~='mention'] {
  background-color: white;
  border: 1px solid #e5e7eb;
  border-radius: 0.375rem;
  box-shadow: 0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1);
  padding: 0;
}

.tippy-box[data-theme~='mention'] > .tippy-content {
  padding: 0;
}

.tippy-box[data-theme~='mention'] > .tippy-arrow {
  display: none;
}
`;

// Inject styles if not already present
if (typeof document !== 'undefined') {
  const styleId = 'tippy-mention-styles';
  if (!document.getElementById(styleId)) {
    const style = document.createElement('style');
    style.id = styleId;
    style.textContent = tippyStyles;
    document.head.appendChild(style);
  }
}
